import {
  getDownloadURL,
  listAll,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { firebaseStorage } from "../firebase/firebase-config";
import uuidv4 from "uuid-random";
import { collection, doc, getDocs, query, setDoc, updateDoc } from "firebase/firestore";
import { firestoreDB } from "../firebase/firebase-config";
import { ReEvent, ReSession, UserDetails } from "../interfaces/types";
import dayjs from "dayjs";

// Function to upload an image to Firebase Storage
export async function uploadImage(
  file: File,
  saveLocation: string
): Promise<string> {
  // Throw an error if no file is provided
  if (!file) throw new Error("No file provided");

  // Create a storage reference with a unique identifier
  const storageReference = storageRef(
    firebaseStorage,
    `${saveLocation}/${uuidv4()}`
  );

  // Upload the file to the storage reference
  const snapshot = await uploadBytes(storageReference, file);
  // Get the download URL of the uploaded file
  const downloadURL = await getDownloadURL(snapshot.ref);
  // Return the download URL
  return downloadURL;
}

// Function to get all images from a specific folder in Firebase Storage
export async function getAllImagesFromFolder(
  folder: string
): Promise<string[]> {
  // Create a storage reference for the specified folder
  const storageReference = storageRef(firebaseStorage, folder);
  try {
    const listResult = await listAll(storageReference);
    const downloadURLs = await Promise.all(
      listResult.items.map(async (item) => {
        return await getDownloadURL(item);
      })
    );
    return downloadURLs;
  } catch (error) {
    console.error("Error getting images from folder:", error);
    throw error;
  }
}

// Function to upload an avatar image to Firebase Storage
export async function uploadAvatar(
  file: File,
  userDetails: UserDetails
): Promise<string> {
  console.log("Uploading avatar:", file);
  if (!file) throw new Error("No file provided");

  // Create a storage reference for the avatar image
  const storageReference = storageRef(
    firebaseStorage,
    `avatars/${userDetails?.uid}`
  );
  try {
    const snapshot = await uploadBytes(storageReference, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log("File uploaded successfully. Download URL:", downloadURL);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
}

// Function to fetch all events from Firestore
export const fetchAllEvents = async (): Promise<ReEvent[]> => {
  try {
    const q = query(collection(firestoreDB, "events"));
    const querySnapshot = await getDocs(q);
    const newEvents: ReEvent[] = [];
    querySnapshot.forEach((doc) => {
      newEvents.push(doc.data() as ReEvent);
    });
    return newEvents;
  } catch (error) {
    console.error("Error fetching events:", error);
    throw error;
  }
};

// Function to add or update an event in Firestore
export const addOrUpdateEventInFirebase = async (
  event: ReEvent
): Promise<void> => {
  try {
    await setDoc(doc(firestoreDB, "events", event.id.toString()), {

      id: event.id,
      name: event.name,
      description: event.description,
      startDate: dayjs(event.startDate).isValid() ? dayjs(event.startDate).toISOString() : '',
      endDate: dayjs(event.endDate).isValid() ? dayjs(event.endDate).toISOString() : '',
      sessions: event.sessions || [],
      eventConfiguration: {
        dashboardTitle: event.eventConfiguration.dashboardTitle,
        dashboardWelcomeText: event.eventConfiguration.dashboardWelcomeText,
        dashboardPictureUrl: event.eventConfiguration.dashboardPictureUrl,
        logoUrl: event.eventConfiguration.logoUrl,
        faviconUrl: event.eventConfiguration.faviconUrl,
        websiteUrl: event.eventConfiguration.websiteUrl,
        targetAudience: event.eventConfiguration.targetAudience,
        submissionStartDate: dayjs(event.eventConfiguration.submissionStartDate).isValid() ? dayjs(event.eventConfiguration.submissionStartDate).toISOString() : '',
        submissionEndDate: dayjs(event.eventConfiguration.submissionEndDate).isValid() ? dayjs(event.eventConfiguration.submissionEndDate).toISOString() : '',
        votingStartDate: dayjs(event.eventConfiguration.votingStartDate).isValid() ? dayjs(event.eventConfiguration.votingStartDate).toISOString() : '',
        votingEndDate: dayjs(event.eventConfiguration.votingEndDate).isValid() ? dayjs(event.eventConfiguration.votingEndDate).toISOString() : '',
        programDate: dayjs(event.eventConfiguration.programDate).isValid() ? dayjs(event.eventConfiguration.programDate).toISOString() : '',
        hasCustomStyling: event.eventConfiguration.hasCustomStyling,
        primaryColor: event.eventConfiguration.primaryColor,
        secondaryColor: event.eventConfiguration.secondaryColor,
        tertiaryColor: event.eventConfiguration.tertiaryColor,
        warningColor: event.eventConfiguration.warningColor,
        infoColor: event.eventConfiguration.infoColor,
        errorColor: event.eventConfiguration.errorColor,
        linkColor: event.eventConfiguration.linkColor,
        clickedLinkColor: event.eventConfiguration.clickedLinkColor,
        topics: event.eventConfiguration.topics,
        roomSetups: event.eventConfiguration.roomSetups,
        possibleDurations: event.eventConfiguration.possibleDurations,
        useXpFactor: event.eventConfiguration.useXpFactor,
        areReviewsAndCommentsVisible: event.eventConfiguration.areReviewsAndCommentsVisible,
        isReviewCreationActive: event.eventConfiguration.isReviewCreationActive,
        isCommentCreationActive: event.eventConfiguration.isCommentCreationActive,
      }

    });
  } catch (error) {
    console.error("Error adding or updating event:", error);
    throw error;
  }
};


export const fetchPresenters = async () => {
  try {
    const presentersCollection = collection(firestoreDB, "presenters");
    const presentersSnapshot = await getDocs(presentersCollection);
    const presentersList = presentersSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        name: `${data.firstName} ${data.lastName}`,
      };
    });
    return presentersList;
  } catch (error) {
    console.error("Error fetching presenters: ", error);
  }
};

export const fetchUsers = async () => {
  try {
    const usersCollection = collection(firestoreDB, "users");
    const usersSnapshot = await getDocs(usersCollection);
    const userdata = usersSnapshot.docs.map((doc) => doc.data() as UserDetails);
    return userdata;
  } catch (error) {
    console.error("Error fetching presenters: ", error);
  }
};

export const addOrUpdateSessionInFirebase = async (session: ReSession) => {
  try {
    await setDoc(doc(firestoreDB, "sessions", session.id.toString()), {
      id: session.id,
      title: session.title,
      subtitle: session.subtitle,
      shortDescription: session.shortDescription,
      longDescription: session.longDescription,
      topic: session.topic,
      type: session.type,
      status: session.status,
      duration: session.duration,
      experienceLevel: session.experienceLevel,
      intendedAudience: session.intendedAudience,
      laptopsRequired: session.laptopsRequired,
      materialDescription: session.materialDescription,
      materialUrl: session.materialUrl,
      materialsNeeded: session.materialsNeeded,
      maxParticipants: session.maxParticipants,
      otherLimitations: session.otherLimitations,
      outlineOrTimetable: session.outlineOrTimetable,
      roomSetup: session.roomSetup,
      sessionGoal: session.sessionGoal,
      xpFactor: session.xpFactor,
      createdAt: dayjs(session.createdAt).toISOString(),
      createdBy: session.createdBy,
      updatedAt: dayjs(session.updatedAt).toISOString(),
      updatedBy: session.updatedBy,
      presenterIds: session.presenterIds,
    });
  } catch (error) {
    console.error("Error adding or updating session:", error);
    throw error;
  }
}

export const fetchAllSessions = async () => {
  try {
    const sessionsCollection = collection(firestoreDB, "sessions");
    const sessionsSnapshot = await getDocs(sessionsCollection);
    const sessionsList = sessionsSnapshot.docs.map((doc) => doc.data());
    return sessionsList;
  } catch (error) {
    console.error("Error fetching sessions: ", error);
  }
};

export const updateUserDetailsInFirebase = async (userDetails: UserDetails) => {
  try {
    const userDoc = doc(firestoreDB, "users", userDetails.uid);
    await updateDoc(userDoc, {
      uid: userDetails.uid,
      email: userDetails.email,
      username: userDetails.username,
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      bio: userDetails.bio,
      professions: userDetails.professions,
      roles: userDetails.roles,
      createdAt: userDetails.createdAt,
      profileImageUrl: userDetails.profileImageUrl,
      eventRoles: userDetails.eventRoles,
    });
  } catch (error) {
    console.error("Error updating user details:", error);
    throw error;
  }
}

export const fetchAllReviews = async () => {
  try {
    const reviewCollection = collection(firestoreDB, "reviews");
    const reviewSnapshot = await getDocs(reviewCollection);
    const reviewList = reviewSnapshot.docs.map((doc) => doc.data());
    return reviewList;
  } catch (error) {
    console.error("Error fetching sessions: ", error);
  }
}

export const addOrUpdateReviewInFirebase = async (review: any) => {
  try {
    await setDoc(doc(firestoreDB, "reviews", review.id.toString()), {
      id: review.id,
      sessionId: review.sessionId,
      createdBy: review.createdBy,
      createdAt: dayjs(review.createdAt).toISOString(),
      whatIlike: review.whatIlike,
      improvementOpportunities: review.improvementOpportunities,
      xpFactor: review.xpFactor,
      score: review.score,
      isNew: review.isNew,
    });
  } catch (error) {
    console.error("Error adding or updating review:", error);
    throw error;
  }
}

export const fetchAllComments = async () => {
  try {
    const commentCollection = collection(firestoreDB, "comments");
    const commentSnapshot = await getDocs(commentCollection);
    const commentList = commentSnapshot.docs.map((doc) => doc.data());
    return commentList;
  } catch (error) {
    console.error("Error fetching comments: ", error);
  }
}

export const addOrUpdateCommentInFirebase = async (comment: any) => {
  try {
    await setDoc(doc(firestoreDB, "comments", comment.id.toString()), {
      id: comment.id,
      reviewId: comment.reviewId,
      content: comment.content,
      createdBy: comment.createdBy,
      createdAt: dayjs(comment.createdAt).toISOString(),
      isNew: comment.isNew,
    });
  } catch (error) {
    console.error("Error adding or updating comment:", error);
    throw error;
  }
}
